import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowDownLeft, ArrowUp, ArrowUpRight, Check, Info } from "lucide-react";
import V2BusyFrame from "./V2BusyFrame";
import V2PlayerAvatar from "./V2PlayerAvatar";
import V2PlayerScaleTooltip from "./V2PlayerScaleTooltip";
import V2PrioritySection from "./V2PrioritySection";
import { foldedLeftPercent, formatScore, V2_ZONES } from "./v2OverviewUtils";

function tipAlign(left) {
  if (left < 18) return "start";
  if (left > 82) return "end";
  return "center";
}

const ZONE_ICONS = {
  underload: ArrowDownLeft,
  suboptimal: ArrowDown,
  optimal: Check,
  supraoptimal: ArrowUp,
  overload: ArrowUpRight,
};

const SCALE_TICKS = [
  { label: "0", left: 0 },
  { label: "4", left: 20 },
  { label: "8", left: 40 },
  { label: "10", left: 50 },
  { label: "9", left: 58 },
  { label: "4", left: 80 },
  { label: "0", left: 100 },
];

export default function V2TrainabilitySection({ players, clubId, date, period, loading = false }) {
  const { t } = useTranslation();
  const [hoveredId, setHoveredId] = useState(null);

  const counts = useMemo(
    () =>
      V2_ZONES.map((zone) => ({
        ...zone,
        count: players.filter((player) => player.zone === zone.key).length,
      })),
    [players]
  );

  return (
    <V2BusyFrame loading={loading} label={t("v2.overview.fetching")}>
    <div>
      <div
        className="mb-4 mt-0.5 h-px bg-[linear-gradient(90deg,transparent,rgba(98,94,166,0.42),transparent)]"
        aria-hidden="true"
      />

      <div className="mb-3 flex items-center justify-between gap-4">
        <strong className="inline-flex items-center gap-1.5 text-[14px] font-bold text-[#f4f2ff]">
          {t("v2.overview.scale_title")}
          <span className="inline-flex text-[#8b86bc]" title={t("v2.overview.trainability_help")}>
            <Info size={13} />
          </span>
        </strong>
        <span className="text-[12px] text-[#8f8abf]">{t("v2.overview.trainability_meta", { count: players.length })}</span>
      </div>

      <div className="v2-folded-scale" aria-label={t("v2.overview.players_distribution")}>
        <div className="v2-scale-board">
          <div className="v2-scale-wash" aria-hidden="true" />
          <div className="v2-scale-columns">
            {counts.map((zone) => {
              const Icon = ZONE_ICONS[zone.key];
              return (
                <div key={zone.key} className={`v2-scale-zone is-${zone.key}`}>
                  <header>
                    <span>
                      {Icon && <Icon size={13} strokeWidth={2.2} />}
                      {t(`v2.overview.${zone.key}`)}
                    </span>
                    <small>{t("v2.overview.players_count", { count: zone.count })}</small>
                  </header>
                </div>
              );
            })}
          </div>

          <div className="v2-scale-players">
            {players.map((player) => {
              const left = foldedLeftPercent(player.score, player.zone);
              const isOpen = hoveredId === player.id;
              return (
                <button
                  key={player.id}
                  type="button"
                  className={`v2-scale-dot${isOpen ? " is-open" : ""}`}
                  style={{
                    left: `${left}%`,
                    bottom: `${6 + player.stack * 32}px`,
                  }}
                  aria-label={`${player.name} · ${formatScore(player.score)}/10`}
                  onMouseEnter={() => setHoveredId(player.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onFocus={() => setHoveredId(player.id)}
                  onBlur={() => setHoveredId(null)}
                >
                  <V2PlayerAvatar
                    name={player.name}
                    color={player.color}
                    zone={player.zone}
                    clubId={clubId}
                    playerId={player.id}
                  />
                  {isOpen && <V2PlayerScaleTooltip player={player} align={tipAlign(left)} />}
                </button>
              );
            })}
          </div>

          <div className="v2-scale-rainbow" />
        </div>

        <div className="v2-scale-axis">
          {SCALE_TICKS.map((tick, index) => (
            <span
              key={`${tick.label}-${index}`}
              style={{ left: `${tick.left}%` }}
              data-edge={tick.left === 0 ? "start" : tick.left === 100 ? "end" : "mid"}
            >
              {tick.label}
            </span>
          ))}
        </div>
      </div>

      <V2PrioritySection players={players} clubId={clubId} date={date} period={period} loading={loading} />
    </div>
    </V2BusyFrame>
  );
}
